import React, { useState, useEffect, useCallback } from "react";
import { API, useAuth } from "../App";
import { toast } from "sonner";
import { ArrowLeft, X, Send } from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════════════
// PROPOSAL BUILDER - Tactical Dark Interface
// Auto-populates from estimates, fully editable, generates white PDF for clients
// NO ICONS - Text labels only (per user requirement)
// ═══════════════════════════════════════════════════════════════════════════════

const ProposalBuilder = ({ 
  estimateId, 
  estimateData, 
  clientInfo, 
  companySettings,
  changeOrderId,  // NEW: If provided, this is a change order proposal
  changeOrderData, // NEW: The specific change order data
  onClose,
  onPreviewSend 
}) => {
  // Get current user for sender info
  const { user } = useAuth();
  
  // Determine if this is a change order proposal
  const isChangeOrder = !!changeOrderId;
  
  // Proposal state
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Display mode for scope section
  const [displayMode, setDisplayMode] = useState("bulleted"); // lump_sum, bulleted, item_names, item_totals, full_breakdown
  
  // Financing toggle
  const [financingEnabled, setFinancingEnabled] = useState(false);
  
  // Estimated duration (editable)
  const [estimatedDuration, setEstimatedDuration] = useState({ min: 10, max: 14 });
  const [durationUnit, setDurationUnit] = useState("Days"); // "Days" or "Weeks"
  
  // Payment schedule as array (for add/delete/edit)
  const [paymentMilestones, setPaymentMilestones] = useState([
    { id: 1, name: "Deposit (Start Date Reserved)", description: "Due upon contract signing", percent: 25 },
    { id: 2, name: "Mid-Project Milestone", description: "Due at rough-in completion", percent: 40 },
    { id: 3, name: "Substantial Completion", description: "Due at finishes completion", percent: 30 },
    { id: 4, name: "Final Closeout", description: "Due upon project completion", percent: 5 }
  ]);
  
  // Edit modals state
  const [editModal, setEditModal] = useState({ open: false, section: null, data: null });
  
  // Milestones (auto-calculated from estimate)
  const [milestones, setMilestones] = useState([]);
  
  // Send modal state
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [sendingVia, setSendingVia] = useState(null); // 'email', 'sms', 'link'
  const [linkCopied, setLinkCopied] = useState(false);
  
  // ChatGPT Generation state
  const [generatingWithAI, setGeneratingWithAI] = useState(false);
  const [aiGeneratedData, setAiGeneratedData] = useState(null);
  
  // Generate timeline and milestones using ChatGPT
  const generateWithChatGPT = async () => {
    if (!estimateId && !effectiveEstimateData) {
      toast.error("No estimate data available");
      return;
    }
    
    setGeneratingWithAI(true);
    try {
      const { total } = calculateTotals();
      
      // Prepare project data for ChatGPT
      const projectData = {
        project_type: effectiveEstimateData?.project_type || "remodel",
        square_footage: effectiveEstimateData?.square_footage || 1000,
        job_name: effectiveEstimateData?.job_name || clientInfo?.name || "Project",
        scope_notes: effectiveEstimateData?.project_description || "",
      };
      
      const materialsData = {
        total_cost: total * 0.4, // Estimate materials as 40% of total
        total_items: effectiveEstimateData?.groups?.reduce((sum, g) => sum + (g.items?.length || 0), 0) || 50,
      };
      
      const response = await API.post(`/mission-brief/generate/${estimateId || 'temp_estimate'}`, {
        project_data: projectData,
        materials_data: materialsData,
        contract_value: total || 50000,
        profit_margin: 20,
      });
      
      if (response.data.documents) {
        const docs = response.data.documents;
        setAiGeneratedData(docs);
        
        // Update timeline from client_proposal
        const timeline = docs.client_proposal?.timeline;
        if (timeline) {
          const workingDays = timeline.working_days;
          setEstimatedDuration({
            min: workingDays?.low || 44,
            max: workingDays?.high || 54,
          });
        }
        
        // Update milestones from operator_order phases
        const phases = docs.operator_order?.phases;
        if (phases && phases.length > 0) {
          const newMilestones = phases.slice(0, 4).map((phase, idx) => ({
            name: phase.phase_name,
            dayRange: phase.day_range || `Days ${idx * 7 + 1}-${(idx + 1) * 7}`,
            scope: phase.daily_objectives?.slice(0, 4) || phase.tasks?.slice(0, 4) || [
              "Phase tasks",
              "Quality checkpoints",
              "Inspections",
              "Documentation"
            ]
          }));
          setMilestones(newMilestones);
        }
        
        // Update payment milestones from milestone_objectives
        const draws = docs.milestone_objectives?.draws;
        if (draws && draws.length > 0) {
          const newPaymentMilestones = draws.map((draw, idx) => ({
            id: idx + 1,
            name: draw.milestone_name,
            description: draw.completion_criteria?.slice(0, 2).join(", ") || `Due at ${draw.target_date}`,
            percent: draw.percentage,
          }));
          setPaymentMilestones(newPaymentMilestones);
        }
        
        toast.success("Timeline & milestones generated by ChatGPT", {
          description: `${timeline?.recommended_duration || 'Timeline'} • ${phases?.length || 0} phases`,
        });
      }
    } catch (error) {
      console.error("ChatGPT generation failed:", error);
      toast.error("Failed to generate with AI. Using defaults.");
    } finally {
      setGeneratingWithAI(false);
    }
  };

  
  // Editable content
  const [projectTitle, setProjectTitle] = useState("");
  const [clientMessage, setClientMessage] = useState("");
  const [projectOverview, setProjectOverview] = useState("");
  const [benefits, setBenefits] = useState([
    { title: "Veteran-Owned", description: "Proudly serving our community with the same dedication and integrity we brought to our service." },
    { title: "Licensed & Insured", description: "Fully licensed and insured for your protection and peace of mind." },
    { title: "30+ Years Experience", description: "Decades of expertise ensuring quality craftsmanship on every project." }
  ]);
  const [termsContent, setTermsContent] = useState("");
  
  // Attached files state
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [attachModalOpen, setAttachModalOpen] = useState(false);
  const fileInputRef = React.useRef(null);
  
  // State for groups fetched from backend (fallback when props are stale)
  const [fetchedGroups, setFetchedGroups] = useState([]);
  
  // Check if props have groups
  const propsHaveGroups = (estimateData?.groups?.length > 0) || 
                          (estimateData?.allScopes?.base?.groups?.length > 0);
  
  // Fetch estimate data from backend when props are empty (fixes stale state issue)
  useEffect(() => {
    const fetchEstimateData = async () => {
      // Only fetch if we have an estimateId but props are empty
      if (estimateId && !propsHaveGroups && !isChangeOrder) {
        console.log("[ProposalBuilder] Props are empty, fetching from backend...");
        try {
          const response = await API.get(`/estimates/${estimateId}/frontend-scopes`);
          const scopes = response.data?.scopes;
          if (scopes?.base?.groups && scopes.base.groups.length > 0) {
            console.log("[ProposalBuilder] Fetched groups from backend:", scopes.base.groups.length);
            setFetchedGroups(scopes.base.groups);
          }
        } catch (error) {
          console.warn("[ProposalBuilder] Could not fetch estimate data:", error);
        }
      }
    };
    
    fetchEstimateData();
  }, [estimateId, propsHaveGroups, isChangeOrder]);
  
  // Merge props with fetched data
  const effectiveEstimateData = React.useMemo(() => {
    if (propsHaveGroups) return estimateData;
    if (fetchedGroups.length > 0) {
      return {
        ...estimateData,
        groups: fetchedGroups,
        allScopes: { base: { groups: fetchedGroups }, changeOrders: [] }
      };
    }
    return estimateData;
  }, [estimateData, fetchedGroups, propsHaveGroups]);
  
  // Calculate totals from estimate
  const calculateTotals = useCallback(() => {
    if (!effectiveEstimateData) return { total: 0, itemCount: 0, categories: [] };
    
    let total = 0;
    let itemCount = 0;
    const categories = [];
    
    // If this is a change order proposal, only include the specific change order
    if (isChangeOrder && changeOrderData) {
      (changeOrderData.groups || []).forEach(group => {
        let categoryTotal = 0;
        const items = [];
        
        (group.items || []).forEach(item => {
          const qty = parseFloat(item.quantity) || 0;
          const unitCost = parseFloat(item.unitCost) || 0;
          const markup = parseFloat(item.markupPercent) || 0;
          const basePrice = qty * unitCost;
          const itemTotal = basePrice * (1 + markup / 100);
          
          categoryTotal += itemTotal;
          itemCount++;
          
          items.push({
            name: item.itemName || item.name || "Item",
            description: item.description || "",
            quantity: qty,
            unit: item.unit || "EA",
            unitCost: unitCost,
            total: itemTotal,
            markup: markup
          });
        });
        
        if (items.length > 0) {
          categories.push({
            name: group.name || "Category",
            total: categoryTotal,
            items: items,
            bulletSummary: generateBulletSummary(group.name, items)
          });
          total += categoryTotal;
        }
      });
      
      return { total, itemCount, categories };
    }
    
    // For main estimate proposal - only include base estimate (NOT change orders)
    const groups = effectiveEstimateData.groups || effectiveEstimateData.allScopes?.base?.groups || [];
    
    groups.forEach(group => {
      let categoryTotal = 0;
      const items = [];
      
      (group.items || []).forEach(item => {
        const qty = parseFloat(item.quantity) || 0;
        const unitCost = parseFloat(item.unitCost) || 0;
        const markup = parseFloat(item.markupPercent) || 0;
        const basePrice = qty * unitCost;
        const itemTotal = basePrice * (1 + markup / 100);
        
        categoryTotal += itemTotal;
        itemCount++;
        
        items.push({
          name: item.itemName || item.name || "Item",
          description: item.description || "",
          quantity: qty,
          unit: item.unit || "EA",
          unitCost: unitCost,
          total: itemTotal,
          markup: markup
        });
      });
      
      if (items.length > 0) {
        categories.push({
          name: group.name || "Category",
          total: categoryTotal,
          items: items,
          bulletSummary: generateBulletSummary(group.name, items)
        });
        total += categoryTotal;
      }
    });
    
    // NOTE: Change orders are now handled as SEPARATE proposals, not included here
    
    return { total, itemCount, categories };
  }, [effectiveEstimateData, isChangeOrder, changeOrderData]);
  
  // Generate bullet summary for a category
  const generateBulletSummary = (categoryName, items) => {
    const name = (categoryName || "").toLowerCase();
    
    if (name.includes("demo")) {
      return items.map(i => `Remove and dispose of ${i.name.toLowerCase()}`).join(". ");
    }
    if (name.includes("drywall")) {
      return "Install and finish drywall including taping, mudding, and sanding to smooth finish.";
    }
    if (name.includes("paint")) {
      return "Apply premium interior paint with proper preparation including priming and multiple coats.";
    }
    if (name.includes("plumb")) {
      return "Complete plumbing installation including fixtures, supply lines, and drainage connections.";
    }
    if (name.includes("elec")) {
      return "Electrical installation including outlets, switches, lighting, and code-compliant wiring.";
    }
    if (name.includes("tile")) {
      return "Professional tile installation with proper substrate preparation and grouting.";
    }
    if (name.includes("floor")) {
      return "Flooring installation including subfloor preparation and finishing details.";
    }
    if (name.includes("fram")) {
      return "Structural framing work including walls, supports, and necessary structural elements.";
    }
    if (name.includes("trim") || name.includes("cabinet")) {
      return "Finish carpentry including trim, moldings, and cabinet installation.";
    }
    
    return items.map(i => i.name).join(", ");
  };
  
  // Auto-calculate milestones from estimate
  const calculateMilestones = useCallback(() => {
    const { categories, total } = calculateTotals();
    const estimatedDays = Math.max(10, Math.ceil(total / 2000)); // Rough estimate: $2000/day
    
    // Group categories into phases
    const phases = [
      { 
        name: "Demo & Prep", 
        dayRange: `Days 1-${Math.ceil(estimatedDays * 0.15)}`,
        scope: categories.filter(c => 
          c.name.toLowerCase().includes("demo") || 
          c.name.toLowerCase().includes("prep")
        ).map(c => c.bulletSummary || c.name).slice(0, 4)
      },
      { 
        name: "Rough-In", 
        dayRange: `Days ${Math.ceil(estimatedDays * 0.15) + 1}-${Math.ceil(estimatedDays * 0.45)}`,
        scope: categories.filter(c => 
          c.name.toLowerCase().includes("plumb") || 
          c.name.toLowerCase().includes("elec") ||
          c.name.toLowerCase().includes("fram") ||
          c.name.toLowerCase().includes("hvac")
        ).map(c => c.bulletSummary || c.name).slice(0, 4)
      },
      { 
        name: "Finishes", 
        dayRange: `Days ${Math.ceil(estimatedDays * 0.45) + 1}-${Math.ceil(estimatedDays * 0.85)}`,
        scope: categories.filter(c => 
          c.name.toLowerCase().includes("drywall") || 
          c.name.toLowerCase().includes("paint") ||
          c.name.toLowerCase().includes("tile") ||
          c.name.toLowerCase().includes("floor") ||
          c.name.toLowerCase().includes("trim") ||
          c.name.toLowerCase().includes("cabinet")
        ).map(c => c.bulletSummary || c.name).slice(0, 4)
      },
      { 
        name: "Final Walkthrough", 
        dayRange: `Days ${Math.ceil(estimatedDays * 0.85) + 1}-${estimatedDays}`,
        scope: [
          "Final inspection and punch list review",
          "Touch-ups and corrections",
          "Client walkthrough and approval",
          "Project documentation and warranty"
        ]
      }
    ];
    
    // Fill in empty phases with generic scope
    phases.forEach(phase => {
      if (phase.scope.length === 0) {
        if (phase.name === "Demo & Prep") {
          phase.scope = ["Site preparation and protection", "Demolition of existing materials", "Debris removal and disposal", "Area preparation for new work"];
        } else if (phase.name === "Rough-In") {
          phase.scope = ["Structural modifications", "Plumbing rough-in", "Electrical rough-in", "HVAC connections"];
        } else if (phase.name === "Finishes") {
          phase.scope = ["Wall and ceiling finishes", "Flooring installation", "Paint and trim work", "Fixture installation"];
        }
      }
    });
    
    return phases;
  }, [calculateTotals]);
  
  // Load or create proposal
  useEffect(() => {
    const loadProposal = async () => {
      // CRITICAL FIX: If we have live data (estimateData.groups) but no estimateId,
      // use the live data directly instead of trying to fetch from database
      const hasLiveData = effectiveEstimateData?.groups?.length > 0;
      
      if (!estimateId && !hasLiveData) {
        // No ID and no data - nothing to show
        setLoading(false);
        return;
      }
      
      if (!estimateId && hasLiveData) {
        // No ID but we have live data - use it directly without fetching
        console.log("[ProposalBuilder] Using live data (no estimateId)");
        
        // Set up a temporary proposal object
        const tempProposal = {
          proposal_id: `temp-${Date.now()}`,
          estimate_id: null,
          title: clientInfo?.project_name || "Project Proposal",
          client_message: "",
          display_default_mode: "bulleted"
        };
        setProposal(tempProposal);
        setProjectTitle(tempProposal.title);
        
        // Generate overview text
        const jobName = clientInfo?.project_name || "your project";
        const overviewText = `This comprehensive proposal outlines our complete plan for ${jobName}. Our experienced team of craftsmen will deliver exceptional quality work, using premium materials and proven techniques.`;
        setProjectOverview(overviewText);
        
        setLoading(false);
        setMilestones(calculateMilestones());
        return;
      }
      
      // We have an estimateId - try to fetch from database
      try {
        // For change orders, look for a CO-specific proposal
        const queryParam = isChangeOrder 
          ? `estimate_id=${estimateId}&change_order_id=${changeOrderId}`
          : `estimate_id=${estimateId}`;
        
        const response = await API.get(`/proposals?${queryParam}`);
        let existingProposal = null;
        
        if (isChangeOrder) {
          // Find proposal for this specific change order
          existingProposal = response.data?.find(p => 
            p.estimate_id === estimateId && p.change_order_id === changeOrderId
          );
        } else {
          // Find main estimate proposal (no change_order_id)
          existingProposal = response.data?.find(p => 
            p.estimate_id === estimateId && !p.change_order_id
          );
        }
        
        if (existingProposal) {
          setProposal(existingProposal);
          setProjectTitle(existingProposal.title || "");
          setClientMessage(existingProposal.client_message || "");
          setDisplayMode(existingProposal.display_default_mode || "bulleted");
          setFinancingEnabled(existingProposal.financing_enabled || false);
          setTermsContent(existingProposal.terms_content || "");
          
          // Load payment milestones if available
          if (existingProposal.payment_milestones) {
            setPaymentMilestones(existingProposal.payment_milestones);
          }
          
          // Load estimated duration if available
          if (existingProposal.estimated_duration) {
            setEstimatedDuration(existingProposal.estimated_duration);
          }
        } else if (hasLiveData) {
          // No saved proposal but we have live data - create proposal on the fly
          try {
            const proposalData = {
              estimate_id: estimateId,
              title: isChangeOrder 
                ? `Change Order #${changeOrderData?.number || ''} - ${clientInfo?.project_name || 'Project'}`
                : (clientInfo?.project_name || "Project Proposal"),
              change_order_id: isChangeOrder ? changeOrderId : null
            };
            
            const createResponse = await API.post("/proposals", proposalData);
            setProposal(createResponse.data);
            setProjectTitle(createResponse.data.title || "");
            setClientMessage(createResponse.data.client_message || "");
          } catch (createErr) {
            console.warn("[ProposalBuilder] Could not create proposal, using temporary:", createErr);
            // Use temporary proposal with live data
            const tempProposal = {
              proposal_id: `temp-${Date.now()}`,
              estimate_id: estimateId,
              title: clientInfo?.project_name || "Project Proposal"
            };
            setProposal(tempProposal);
            setProjectTitle(tempProposal.title);
          }
        } else {
          // No saved proposal and no live data - shouldn't happen but handle gracefully
          console.warn("[ProposalBuilder] No proposal found and no live data");
        }
        
        // Load terms template
        try {
          const termsResponse = await API.get("/settings/terms-templates");
          const defaultTerms = termsResponse.data?.find(t => t.template_type === "company");
          if (defaultTerms && !existingProposal?.terms_content) {
            setTermsContent(defaultTerms.content || "");
          }
        } catch (e) {
          console.log("No terms templates found");
        }
        
        // Generate overview text
        const jobName = clientInfo?.project_name || proposal?.title || "your project";
        const overviewText = isChangeOrder
          ? `This Change Order proposal outlines additional work for ${jobName}. Our team has reviewed the requested modifications and prepared this comprehensive scope of additional work with transparent pricing.`
          : `This comprehensive proposal outlines our complete plan for ${jobName}. Our experienced team of craftsmen will deliver exceptional quality work, using premium materials and proven techniques. We've carefully reviewed every aspect of this project to ensure accurate pricing and realistic timelines.`;
        setProjectOverview(overviewText);
        
      } catch (error) {
        console.error("Failed to load proposal:", error);
        // Don't show error toast if we have live data - just use it
        if (hasLiveData) {
          console.log("[ProposalBuilder] API failed, using live data");
          const tempProposal = {
            proposal_id: `temp-${Date.now()}`,
            estimate_id: estimateId,
            title: clientInfo?.project_name || "Project Proposal"
          };
          setProposal(tempProposal);
          setProjectTitle(tempProposal.title);
        } else {
          toast.error("Failed to load proposal");
        }
      } finally {
        setLoading(false);
      }
    };
    
    loadProposal();
    setMilestones(calculateMilestones());
  }, [estimateId, clientInfo, calculateMilestones, isChangeOrder, changeOrderId, changeOrderData]);

  // ═══════════════════════════════════════════════════════════════
  // LOAD EXISTING CHATGPT DATA ON MOUNT
  // Updates timeline and milestones from stored ChatGPT generation
  // ═══════════════════════════════════════════════════════════════
  useEffect(() => {
    const loadExistingChatGPTData = async () => {
      if (!estimateId) return;
      
      try {
        const response = await API.get(`/estimates/${estimateId}`);
        const missionBrief = response.data?.mission_brief_documents;
        
        if (missionBrief) {
          setAiGeneratedData(missionBrief);
          
          // If we have timeline data, update the estimated duration
          if (missionBrief.client_proposal?.timeline) {
            const timeline = missionBrief.client_proposal.timeline;
            const calendarWeeks = timeline.calendar_weeks || {};
            setEstimatedDuration({
              min: calendarWeeks.low || 10,
              max: calendarWeeks.high || 14
            });
            setDurationUnit("Weeks"); // ChatGPT provides weeks, not days
            console.log("[ProposalBuilder] Loaded ChatGPT timeline:", timeline.recommended_duration);
          }
          
          // If we have milestone data, update payment milestones
          if (missionBrief.milestone_objectives?.draws) {
            const draws = missionBrief.milestone_objectives.draws;
            const chatgptPaymentMilestones = draws.map((draw, idx) => ({
              id: idx + 1,
              name: draw.milestone_name || `Draw ${idx + 1}`,
              percent: draw.percentage || 25,
              description: draw.completion_criteria?.[0] || "Per contract terms"
            }));
            setPaymentMilestones(chatgptPaymentMilestones);
            console.log("[ProposalBuilder] Loaded ChatGPT milestones:", chatgptPaymentMilestones.length, "draws");
          }
          
          // If we have scope phases from operator order
          if (missionBrief.operator_order?.phases) {
            console.log("[ProposalBuilder] Loaded ChatGPT phases:", missionBrief.operator_order.phases.length, "phases");
          }
        }
      } catch (error) {
        console.log("[ProposalBuilder] No existing ChatGPT data");
      }
    };
    
    loadExistingChatGPTData();
  }, [estimateId]);
  
  // Save proposal updates
  const saveProposal = async (updates) => {
    if (!proposal?.proposal_id) return;
    
    setSaving(true);
    try {
      await API.put(`/proposals/${proposal.proposal_id}`, updates);
      setProposal(prev => ({ ...prev, ...updates }));
      toast.success("Proposal saved");
    } catch (error) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };
  
  // Payment schedule validation
  const paymentTotal = paymentMilestones.reduce((sum, m) => sum + m.percent, 0);
  const paymentValid = paymentTotal === 100;
  
  // Calculate amounts
  const { total: grandTotal, categories } = calculateTotals();
  const estimatedDays = Math.max(10, Math.ceil(grandTotal / 2000));
  
  // Financing calculation (5% APR, 60 months)
  const monthlyPayment = financingEnabled ? ((grandTotal * 1.05) / 60).toFixed(2) : 0;
  
  // Format currency
  const fmt = (n) => `$${(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  
  // Edit modal handler
  const openEditModal = (section, data) => {
    setEditModal({ open: true, section, data });
  };
  
  const closeEditModal = () => {
    setEditModal({ open: false, section: null, data: null });
  };
  
  const saveEditModal = (newData) => {
    switch (editModal.section) {
      case "title":
        setProjectTitle(newData);
        saveProposal({ title: newData });
        break;
      case "client":
        // Update client info through proposal
        saveProposal({
          client_name: newData.name,
          client_email: newData.email,
          client_phone: newData.phone
        });
        break;
      case "message":
        setClientMessage(newData);
        saveProposal({ client_message: newData });
        break;
      case "overview":
        setProjectOverview(newData);
        break;
      case "milestone":
        const updatedMilestones = [...milestones];
        updatedMilestones[newData.index] = newData.milestone;
        setMilestones(updatedMilestones);
        break;
      case "benefit":
        const updatedBenefits = [...benefits];
        updatedBenefits[newData.index] = newData.benefit;
        setBenefits(updatedBenefits);
        break;
      case "terms":
        setTermsContent(newData);
        saveProposal({ terms_content: newData });
        break;
      case "duration":
        setEstimatedDuration(newData);
        break;
      default:
        break;
    }
    closeEditModal();
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#020617" }}>
        <div className="text-emerald-400 text-lg">Loading proposal...</div>
      </div>
    );
  }
  
  // Check if we have data to work with (either estimateId OR estimateData with groups)
  const hasEstimateData = estimateId || (effectiveEstimateData?.groups?.length > 0);
  
  // EMPTY STATE: No estimate data available
  if (!hasEstimateData) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ backgroundColor: "#020617" }}>
        <div className="text-center p-8 rounded-lg" style={{ backgroundColor: "#0f172a", border: "2px dashed #334155", maxWidth: "500px" }}>
          <h2 className="text-xl font-bold text-white mb-4">No Estimate Data</h2>
          <p className="text-slate-400 mb-6">Create an estimate first using the ESTIMATING COMMAND, then return here to build your proposal.</p>
          <button
            onClick={onClose}
            className="px-6 py-3 rounded font-bold text-white transition-all hover:scale-105"
            style={{ backgroundColor: "#10b981" }}
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }
  
  // Handle file upload
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newFiles = files.map(file => ({
      id: Date.now() + Math.random(),
      name: file.name,
      type: file.type,
      size: file.size,
      file: file
    }));
    setAttachedFiles(prev => [...prev, ...newFiles]);
    setAttachModalOpen(false);
  };
  
  // Remove attached file
  const removeAttachedFile = (fileId) => {
    setAttachedFiles(prev => prev.filter(f => f.id !== fileId));
  };
  
  return (
    <div 
      className="min-h-screen overflow-y-auto pt-14 pb-24"
      style={{ 
        backgroundColor: "#020617",
        fontFamily: "'Inter', sans-serif"
      }}
    >
      {/* Fixed Header Bar - Always visible at top */}
      <div 
        className="fixed top-0 left-0 right-0 z-[200] px-6 py-3 flex items-center justify-between"
        style={{ 
          backgroundColor: "#0f172a",
          borderBottom: "2px solid rgba(16, 185, 129, 0.5)",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)"
        }}
      >
        <button
          onClick={onClose}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-800 text-white hover:bg-slate-700 transition-all font-medium"
        >
          <ArrowLeft className="h-4 w-4" />
          <span>← Exit</span>
        </button>
        
        <div className="flex items-center gap-3">
          <span className="text-emerald-400 font-bold">CLIENT PROPOSAL</span>
          <span className="text-slate-500">|</span>
          <span className="text-slate-400">{clientInfo?.project_name || "Project"}</span>
        </div>
        
        <button
          onClick={onClose}
          className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-all"
          title="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Document Container - Centered, Document-like width */}
      <div 
        className="mx-auto mt-4"
        style={{ 
          maxWidth: "900px",
          backgroundColor: "#0f172a",
          borderRadius: "8px",
          border: "1px solid rgba(16, 185, 129, 0.2)",
          boxShadow: "0 0 40px rgba(0, 0, 0, 0.5)"
        }}
      >
      {/* ═══════════════════════════════════════════════════════════════════════════
          SECTION 1: HEADER - Company Info & Proposal Metadata
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div 
        className="px-10 pt-8 pb-6 relative"
        style={{
          background: "linear-gradient(180deg, rgba(16, 185, 129, 0.1) 0%, transparent 100%)",
          borderBottom: "1px solid rgba(16, 185, 129, 0.2)",
          borderRadius: "8px 8px 0 0"
        }}
      >
        {/* Company Name & Info from Settings */}
        <div className="mb-6">
          <h1 
            className="text-2xl font-bold tracking-wide"
            style={{ 
              color: "#10b981",
              fontFamily: "'Playfair Display', serif"
            }}
          >
            {companySettings?.company_name || "Alpha Construction Pros"}
          </h1>
          <p className="text-slate-400 text-sm mt-1">
            {companySettings?.address || "123 Builder Lane, Construction City, ST 12345"}
          </p>
          <p className="text-slate-500 text-xs mt-1">
            {companySettings?.email || "info@company.com"} • {companySettings?.phone || "(555) 123-4567"}
          </p>
        </div>
        
        {/* Proposal Metadata Row */}
        <div className="flex items-center gap-8 flex-wrap">
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-widest">PROPOSAL ID</p>
            <p className="text-white font-bold text-sm" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
              {proposal?.estimate_number || estimateId || "EST-00001"}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-widest">DATE</p>
            <p className="text-white font-bold text-sm">
              {new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-widest">VALID FOR</p>
            <p className="text-white font-bold text-sm">30 Days</p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-widest">PREPARED BY</p>
            <p className="text-white font-bold text-sm">
              {user?.name || user?.email?.split('@')[0] || "Project Manager"}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-widest">STATUS</p>
            <p 
              className="font-bold text-sm px-2 py-0.5 rounded"
              style={{
                backgroundColor: proposal?.status === "sent" ? "rgba(245, 158, 11, 0.2)" : 
                               proposal?.status === "signed" ? "rgba(16, 185, 129, 0.2)" : "rgba(100, 116, 139, 0.2)",
                color: proposal?.status === "sent" ? "#f59e0b" : 
                       proposal?.status === "signed" ? "#10b981" : "#94a3b8"
              }}
            >
              {proposal?.status === "draft" ? "Awaiting Signature" : 
               proposal?.status === "sent" ? "Sent" :
               proposal?.status === "signed" ? "Signed" : "Draft"}
            </p>
          </div>
        </div>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          SECTION 2: TITLE & CLIENT INFO
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="px-10 py-8">
        <p 
          className="text-xs uppercase tracking-[4px] mb-2"
          style={{ color: isChangeOrder ? "#f59e0b" : "#10b981" }}
        >
          {isChangeOrder ? `CHANGE ORDER #${changeOrderData?.number || ''} PROPOSAL` : 'PROJECT PROPOSAL'}
        </p>
        
        {/* Project Title with Edit */}
        <div className="flex items-start justify-between mb-6">
          <h2 
            className="text-3xl font-bold text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {projectTitle || "Complete 10x10 Bathroom Remodel"}
          </h2>
          <button
            onClick={() => openEditModal("title", projectTitle)}
            className="text-xs text-emerald-400 hover:text-emerald-300 px-3 py-1 rounded border border-emerald-500/30 hover:border-emerald-500/50 transition-all"
          >
            Edit
          </button>
        </div>
        
        {/* Info Cards Row */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div 
            className="p-4 rounded"
            style={{
              backgroundColor: "rgba(15, 23, 42, 0.8)",
              borderLeft: "3px solid #10b981"
            }}
          >
            <p className="text-xs text-slate-500 uppercase tracking-wider">ESTIMATED START</p>
            <p className="text-white font-bold text-lg">
              {new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </p>
          </div>
          <div 
            className="p-4 rounded cursor-pointer hover:bg-slate-800/50 transition-colors"
            style={{
              backgroundColor: "rgba(15, 23, 42, 0.8)",
              borderLeft: "3px solid #10b981"
            }}
            onClick={() => openEditModal("duration", estimatedDuration)}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-slate-500 uppercase tracking-wider">ESTIMATED DURATION</p>
                <p className="text-white font-bold text-lg">{estimatedDuration.min}-{estimatedDuration.max} {durationUnit}</p>
              </div>
              <span className="text-slate-500 text-xs">Edit</span>
            </div>
          </div>
          <div 
            className="p-4 rounded"
            style={{
              backgroundColor: "rgba(15, 23, 42, 0.8)",
              borderLeft: "3px solid #10b981"
            }}
          >
            <p className="text-xs text-slate-500 uppercase tracking-wider">WARRANTY</p>
            <p className="text-white font-bold text-lg">1 Year Labor</p>
          </div>
        </div>
        
        {/* Prepared For Box */}
        <div 
          className="p-5 rounded-lg"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.6)",
            border: "1px solid rgba(16, 185, 129, 0.3)",
            borderLeft: "4px solid #10b981"
          }}
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-emerald-400 uppercase tracking-widest mb-3">PREPARED FOR</p>
              <p className="text-white font-bold text-xl mb-1">
                {proposal?.client_name || clientInfo?.name || "Client Name"}
              </p>
              <p className="text-slate-400 text-sm">
                {proposal?.client_email || clientInfo?.email || "client@email.com"}
              </p>
              <p className="text-slate-400 text-sm">
                {proposal?.client_phone || clientInfo?.phone || "(555) 000-0000"}
              </p>
            </div>
            <button
              onClick={() => openEditModal("client", {
                name: proposal?.client_name || clientInfo?.name || "",
                email: proposal?.client_email || clientInfo?.email || "",
                phone: proposal?.client_phone || clientInfo?.phone || ""
              })}
              className="text-xs text-emerald-400 hover:text-emerald-300 px-3 py-1 rounded border border-emerald-500/30 hover:border-emerald-500/50 transition-all"
            >
              Edit
            </button>
          </div>
        </div>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          SECTION 3: CLIENT MESSAGE
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="px-10 py-6">
        <div 
          className="p-6 rounded-lg"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.5)",
            borderTop: "2px solid rgba(16, 185, 129, 0.5)"
          }}
        >
          <div className="flex items-start justify-between mb-4">
            <p className="text-xs text-emerald-400 uppercase tracking-widest">CLIENT MESSAGE</p>
            <button
              onClick={() => openEditModal("message", clientMessage)}
              className="text-xs text-emerald-400 hover:text-emerald-300 px-3 py-1 rounded border border-emerald-500/30 hover:border-emerald-500/50 transition-all"
            >
              Edit
            </button>
          </div>
          <p className="text-slate-300 leading-relaxed whitespace-pre-wrap">
            {clientMessage || `Dear ${clientInfo?.name?.split(" ")[0] || "Valued Client"},

We are delighted to work with you on this project. Our team has carefully reviewed every detail and we're confident that together we can create something truly exceptional. This proposal reflects our commitment to quality craftsmanship, transparent pricing, and delivering results that exceed expectations.

We look forward to bringing your vision to life.`}
          </p>
        </div>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          SECTION 4: PROJECT OVERVIEW & TIMELINE
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="px-10 py-6">
        <div 
          className="p-6 rounded-lg"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.5)",
            borderTop: "2px solid rgba(16, 185, 129, 0.5)"
          }}
        >
          <div className="flex items-start justify-between mb-4">
            <p className="text-xs text-emerald-400 uppercase tracking-widest">PROJECT OVERVIEW</p>
            <div className="flex items-center gap-2">
              <button
                onClick={generateWithChatGPT}
                disabled={generatingWithAI}
                className={`text-xs px-3 py-1 rounded border transition-all flex items-center gap-1.5 ${
                  generatingWithAI 
                    ? "bg-amber-500/20 text-amber-400 border-amber-500/50 cursor-wait" 
                    : "bg-blue-500/20 text-blue-400 border-blue-500/50 hover:bg-blue-500/30"
                }`}
              >
                {generatingWithAI ? (
                  <>
                    <span className="animate-spin">⟳</span>
                    Generating...
                  </>
                ) : (
                  <>
                    ✦ Generate with AI
                  </>
                )}
              </button>
              <button
                onClick={() => openEditModal("overview", projectOverview)}
                className="text-xs text-emerald-400 hover:text-emerald-300 px-3 py-1 rounded border border-emerald-500/30 hover:border-emerald-500/50 transition-all"
              >
                Edit
              </button>
            </div>
          </div>
          <p className="text-slate-300 leading-relaxed mb-8">
            {projectOverview}
          </p>
          
          {/* AI Generated Badge */}
          {aiGeneratedData && (
            <div className="mb-4 flex items-center gap-2">
              <span className="text-xs bg-blue-500/20 text-blue-400 px-2 py-1 rounded-full border border-blue-500/30">
                ✦ Timeline & milestones generated by ChatGPT
              </span>
              {aiGeneratedData.client_proposal?.timeline?.recommended_duration && (
                <span className="text-xs text-slate-400">
                  {aiGeneratedData.client_proposal.timeline.recommended_duration}
                </span>
              )}
            </div>
          )}
          
          {/* Milestone Cards */}
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, idx) => (
              <div 
                key={idx}
                className="p-4 rounded-lg relative"
                style={{
                  backgroundColor: "rgba(15, 23, 42, 0.8)",
                  border: "1px solid rgba(100, 116, 139, 0.3)"
                }}
              >
                <button
                  onClick={() => openEditModal("milestone", { index: idx, milestone })}
                  className="absolute top-2 right-2 text-xs text-slate-500 hover:text-emerald-400 transition-colors"
                >
                  Edit
                </button>
                
                <p 
                  className="text-3xl font-black mb-2"
                  style={{ 
                    color: "#10b981",
                    fontFamily: "'Rajdhani', sans-serif"
                  }}
                >
                  {String(idx + 1).padStart(2, "0")}
                </p>
                <p className="text-white font-bold text-sm mb-1">{milestone.name}</p>
                <p 
                  className="text-lg font-bold mb-3"
                  style={{ color: "#10b981" }}
                >
                  {milestone.dayRange}
                </p>
                <ul className="space-y-1">
                  {(milestone.scope || []).slice(0, 4).map((item, i) => (
                    <li key={i} className="text-slate-400 text-xs flex items-start gap-2">
                      <span className="text-emerald-500 mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          SECTION 5: DETAILED SCOPE & PRICING
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="px-10 py-6">
        <div 
          className="p-6 rounded-lg"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.5)",
            borderTop: "2px solid rgba(16, 185, 129, 0.5)"
          }}
        >
          <div className="flex items-center justify-between mb-6">
            <p className="text-xs text-emerald-400 uppercase tracking-widest">DETAILED SCOPE OF WORK & PRICING</p>
            
            {/* Display Mode Toggle */}
            <div className="flex items-center gap-1 bg-slate-900/50 p-1 rounded">
              {[
                { value: "lump_sum", label: "Lump Sum" },
                { value: "bulleted", label: "Bulleted" },
                { value: "item_names", label: "Item Names" },
                { value: "item_totals", label: "Item Totals" },
                { value: "full_breakdown", label: "Full Breakdown" }
              ].map(mode => (
                <button
                  key={mode.value}
                  onClick={() => {
                    setDisplayMode(mode.value);
                    saveProposal({ display_default_mode: mode.value });
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${
                    displayMode === mode.value 
                      ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/50" 
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Scope Content Based on Display Mode */}
          <div className="space-y-3 mb-6">
            {/* LUMP SUM - Shows category names with bullet descriptions, NO prices until the end */}
            {displayMode === "lump_sum" ? (
              <>
                {categories.map((category, idx) => (
                  <div key={idx}>
                    {/* Category Header - NO PRICE */}
                    <div 
                      className="flex items-center justify-between px-4 py-3 rounded-t"
                      style={{
                        backgroundColor: category.isChangeOrder ? "rgba(245, 158, 11, 0.15)" : "rgba(16, 185, 129, 0.15)",
                        borderLeft: `3px solid ${category.isChangeOrder ? "#f59e0b" : "#10b981"}`
                      }}
                    >
                      <p className="text-white font-bold">{category.name}</p>
                    </div>
                    
                    {/* Category Description - Bullet points, no pricing */}
                    <div 
                      className="px-4 py-3 rounded-b border-x border-b"
                      style={{
                        backgroundColor: "rgba(15, 23, 42, 0.3)",
                        borderColor: "rgba(100, 116, 139, 0.2)"
                      }}
                    >
                      <p className="text-slate-400 text-sm">{category.bulletSummary}</p>
                    </div>
                  </div>
                ))}
                
                {/* Total Investment Box at the end for Lump Sum */}
                <div 
                  className="p-6 rounded-lg text-center mt-6"
                  style={{
                    backgroundColor: "rgba(16, 185, 129, 0.1)",
                    border: "2px solid #10b981"
                  }}
                >
                  <p className="text-xs text-slate-400 uppercase tracking-widest mb-1">Complete Project Scope</p>
                  <p 
                    className="text-4xl font-black text-emerald-400"
                    style={{ fontFamily: "'JetBrains Mono', monospace" }}
                  >
                    {fmt(grandTotal)}
                  </p>
                </div>
              </>
            ) : (
              /* Other display modes - show categories with appropriate detail level */
              categories.map((category, idx) => (
                <div key={idx}>
                  {/* Category Header */}
                  <div 
                    className="flex items-center justify-between px-4 py-3 rounded-t"
                    style={{
                      backgroundColor: category.isChangeOrder ? "rgba(245, 158, 11, 0.15)" : "rgba(16, 185, 129, 0.15)",
                      borderLeft: `3px solid ${category.isChangeOrder ? "#f59e0b" : "#10b981"}`
                    }}
                  >
                    <p className="text-white font-bold">{category.name}</p>
                    {displayMode !== "item_names" && (
                      <p 
                        className="font-bold"
                        style={{ 
                          color: category.isChangeOrder ? "#f59e0b" : "#10b981",
                          fontFamily: "'JetBrains Mono', monospace"
                        }}
                      >
                        {fmt(category.total)}
                      </p>
                    )}
                  </div>
                  
                  {/* Category Items */}
                  <div 
                    className="px-4 py-3 rounded-b border-x border-b"
                    style={{
                      backgroundColor: "rgba(15, 23, 42, 0.3)",
                      borderColor: "rgba(100, 116, 139, 0.2)"
                    }}
                  >
                    {displayMode === "bulleted" ? (
                      /* BULLETED - Category name with total, then bullet description */
                      <p className="text-slate-400 text-sm">{category.bulletSummary}</p>
                    ) : displayMode === "item_names" ? (
                      /* ITEM NAMES - Just item names, no prices */
                      <ul className="space-y-1">
                        {category.items.map((item, i) => (
                          <li key={i} className="text-slate-300 text-sm flex items-start gap-2">
                            <span className="text-emerald-500 mt-0.5">•</span>
                            <span>{item.name}</span>
                          </li>
                        ))}
                      </ul>
                    ) : displayMode === "item_totals" ? (
                      /* ITEM TOTALS - Item names with total price each */
                      <div className="space-y-2">
                        {category.items.map((item, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <span className="text-slate-300 text-sm">{item.name}</span>
                            <span className="text-emerald-400 text-sm" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                              {fmt(item.total)}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      /* FULL BREAKDOWN - Quantity × Unit Price = Total */
                      <div className="space-y-2">
                        {category.items.map((item, i) => (
                          <div key={i} className="flex items-center justify-between">
                            <div>
                              <span className="text-slate-300 text-sm">{item.name}</span>
                              {item.description && (
                                <p className="text-slate-500 text-xs">{item.description}</p>
                              )}
                            </div>
                            <div className="text-right">
                              <span className="text-slate-500 text-xs mr-3">
                                {item.quantity} × {fmt(item.unitCost)} / {item.unit}
                              </span>
                              <span className="text-emerald-400 text-sm font-medium" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                                {fmt(item.total)}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
          
          {/* Investment Box - Only show for non-lump-sum modes since lump sum already has its own total box */}
          {displayMode !== "lump_sum" && (
            <div 
              className="p-6 rounded-lg text-center"
              style={{
                backgroundColor: "rgba(16, 185, 129, 0.1)",
                border: "2px solid #10b981"
              }}
            >
              <p className="text-xs text-emerald-400 uppercase tracking-widest mb-2">YOUR INVESTMENT</p>
              <p 
                className="text-4xl font-black text-emerald-400"
                style={{ fontFamily: "'JetBrains Mono', monospace" }}
              >
                {fmt(grandTotal)}
              </p>
            </div>
          )}
          
          {/* Financing Toggle */}
          <div className="mt-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-slate-400 text-sm">Offer Financing</span>
              <button
                onClick={() => {
                  setFinancingEnabled(!financingEnabled);
                  saveProposal({ financing_enabled: !financingEnabled });
                }}
                className={`w-12 h-6 rounded-full transition-all relative ${
                  financingEnabled ? "bg-emerald-500" : "bg-slate-700"
                }`}
              >
                <span 
                  className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all ${
                    financingEnabled ? "left-7" : "left-1"
                  }`}
                />
              </button>
            </div>
            
            {financingEnabled && (
              <div 
                className="px-4 py-2 rounded"
                style={{ backgroundColor: "rgba(16, 185, 129, 0.1)" }}
              >
                <span className="text-slate-400 text-sm">As low as </span>
                <span className="text-emerald-400 font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  ${monthlyPayment}/mo
                </span>
                <span className="text-slate-500 text-xs ml-1">(60 months)</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          SECTION 6: WHY CHOOSE US
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="px-10 py-6">
        <div 
          className="p-6 rounded-lg"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.5)",
            borderTop: "2px solid rgba(16, 185, 129, 0.5)"
          }}
        >
          <p className="text-xs text-emerald-400 uppercase tracking-widest mb-6">WHY CHOOSE US</p>
          
          <div className="grid grid-cols-3 gap-4">
            {benefits.map((benefit, idx) => (
              <div 
                key={idx}
                className="p-5 rounded-lg relative"
                style={{
                  backgroundColor: "rgba(15, 23, 42, 0.8)",
                  borderLeft: "3px solid #10b981"
                }}
              >
                <button
                  onClick={() => openEditModal("benefit", { index: idx, benefit })}
                  className="absolute top-2 right-2 text-xs text-slate-500 hover:text-emerald-400 transition-colors"
                >
                  Edit
                </button>
                <p className="text-white font-bold mb-2">{benefit.title}</p>
                <p className="text-slate-400 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          SECTION 7: DRAW SCHEDULE
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="px-10 py-6">
        <div 
          className="p-6 rounded-lg"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.5)",
            borderTop: "2px solid rgba(16, 185, 129, 0.5)"
          }}
        >
          <div>
            <p className="text-xs text-emerald-400 uppercase tracking-widest">DRAW SCHEDULE</p>
            <p className="text-slate-500 text-xs mt-1 mb-6">Payment milestones for this project</p>
          </div>
          
          <div className="space-y-3">
            {/* Payment Milestones - Dynamic Array */}
            {paymentMilestones.map((milestone, idx) => (
              <div 
                key={milestone.id}
                className="flex items-center justify-between p-3 rounded-lg"
                style={{ backgroundColor: "rgba(30, 41, 59, 0.3)" }}
              >
                <div className="flex-1">
                  <input
                    type="text"
                    value={milestone.name}
                    onChange={(e) => {
                      const updated = [...paymentMilestones];
                      updated[idx].name = e.target.value;
                      setPaymentMilestones(updated);
                    }}
                    className="text-white font-medium bg-transparent border-none outline-none w-full"
                    placeholder="Milestone name"
                  />
                  <input
                    type="text"
                    value={milestone.description}
                    onChange={(e) => {
                      const updated = [...paymentMilestones];
                      updated[idx].description = e.target.value;
                      setPaymentMilestones(updated);
                    }}
                    className="text-slate-500 text-xs bg-transparent border-none outline-none w-full"
                    placeholder="Description"
                  />
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center">
                    <input
                      type="number"
                      value={milestone.percent}
                      onChange={(e) => {
                        const updated = [...paymentMilestones];
                        updated[idx].percent = parseInt(e.target.value) || 0;
                        setPaymentMilestones(updated);
                      }}
                      className="w-16 px-2 py-1 text-center text-white bg-slate-900 border border-slate-700 rounded"
                      min="0"
                      max="100"
                    />
                    <span className="text-slate-400 ml-1">%</span>
                  </div>
                  <p className="text-emerald-400 font-bold w-24 text-right" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                    {fmt(grandTotal * milestone.percent / 100)}
                  </p>
                  {/* Delete Button */}
                  {paymentMilestones.length > 1 && (
                    <button
                      onClick={() => {
                        setPaymentMilestones(paymentMilestones.filter(m => m.id !== milestone.id));
                      }}
                      className="text-slate-500 hover:text-red-400 transition-colors px-2"
                      title="Delete milestone"
                    >
                      ×
                    </button>
                  )}
                </div>
              </div>
            ))}
            
            {/* Add Payment Button */}
            <button
              onClick={() => {
                const newId = Math.max(...paymentMilestones.map(m => m.id)) + 1;
                setPaymentMilestones([...paymentMilestones, {
                  id: newId,
                  name: "New Milestone",
                  description: "Description",
                  percent: 0
                }]);
              }}
              className="w-full py-3 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2"
              style={{
                backgroundColor: "rgba(16, 185, 129, 0.1)",
                border: "1px dashed rgba(16, 185, 129, 0.4)",
                color: "#10b981"
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(16, 185, 129, 0.2)";
                e.currentTarget.style.borderStyle = "solid";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "rgba(16, 185, 129, 0.1)";
                e.currentTarget.style.borderStyle = "dashed";
              }}
            >
              + Add Payment Milestone
            </button>
            
            {/* Total Line */}
            <div 
              className="flex items-center justify-between pt-4 mt-4"
              style={{ borderTop: "1px solid rgba(100, 116, 139, 0.3)" }}
            >
              <p className="text-slate-400 font-medium">Milestone Total:</p>
              <p className={`font-bold text-lg ${paymentValid ? "text-emerald-400" : "text-red-400"}`}>
                {paymentTotal}%
                {!paymentValid && <span className="text-xs ml-2">(Must equal 100%)</span>}
              </p>
            </div>
            
            <div className="flex items-center justify-between">
              <p className="text-slate-400">First Payment Due:</p>
              <p className="text-emerald-400 font-bold" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                {fmt(grandTotal * (paymentMilestones[0]?.percent || 0) / 100)}
              </p>
            </div>
          </div>
        </div>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          SECTION 7.5: ATTACH FILES (Separate Section)
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="px-10 py-6">
        <div 
          className="p-6 rounded-lg"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.5)",
            borderTop: "2px solid rgba(16, 185, 129, 0.5)"
          }}
        >
          <p className="text-xs text-emerald-400 uppercase tracking-widest mb-4">ATTACHMENTS</p>
          
          {/* Large Green Attach Button */}
          <button
            onClick={() => setAttachModalOpen(true)}
            className="w-full py-4 rounded-lg text-base font-bold uppercase transition-all flex items-center justify-center gap-3"
            style={{
              backgroundColor: "rgba(16, 185, 129, 0.15)",
              border: "2px dashed #10b981",
              color: "#10b981"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(16, 185, 129, 0.25)";
              e.currentTarget.style.borderStyle = "solid";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "rgba(16, 185, 129, 0.15)";
              e.currentTarget.style.borderStyle = "dashed";
            }}
          >
            + Attach Photos & Documents
          </button>
          <p className="text-slate-500 text-xs text-center mt-2">
            Upload photos, PDFs, or Word documents to include with this proposal
          </p>
          
          {/* Show attached files */}
          {attachedFiles.length > 0 && (
            <div className="mt-4 space-y-2">
              {attachedFiles.map(file => (
                <div 
                  key={file.id}
                  className="flex items-center justify-between px-4 py-3 rounded"
                  style={{ backgroundColor: "rgba(30, 41, 59, 0.5)", border: "1px solid rgba(100, 116, 139, 0.2)" }}
                >
                  <span className="text-slate-300 text-sm">{file.name}</span>
                  <button
                    onClick={() => removeAttachedFile(file.id)}
                    className="text-slate-500 hover:text-red-400 text-sm px-2 py-1"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          SECTION 8: TERMS & CONDITIONS
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="px-10 py-6">
        <div 
          className="p-6 rounded-lg"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.5)",
            borderTop: "2px solid rgba(16, 185, 129, 0.5)"
          }}
        >
          <div className="flex items-start justify-between mb-4">
            <p className="text-xs text-emerald-400 uppercase tracking-widest">TERMS & CONDITIONS</p>
            <div className="flex gap-2">
              <button
                onClick={() => openEditModal("terms", termsContent)}
                className="text-xs text-emerald-400 hover:text-emerald-300 px-3 py-1 rounded border border-emerald-500/30 hover:border-emerald-500/50 transition-all"
              >
                Edit
              </button>
              <button
                onClick={() => {
                  // Open Settings/Legal in a new tab or navigate
                  window.open('/settings/system/legal', '_blank');
                }}
                className="text-xs text-slate-400 hover:text-emerald-400 px-3 py-1 rounded border border-slate-500/30 hover:border-emerald-500/50 transition-all"
              >
                Add from Settings → Legal
              </button>
            </div>
          </div>
          <div className="text-slate-400 text-sm whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">
            {termsContent || `1. SCOPE OF WORK: Work shall be performed as specified in this proposal. Any changes to scope must be agreed upon in writing.

2. PAYMENT TERMS: Payment schedule as outlined above. All payments are due upon receipt of invoice.

3. WARRANTY: All workmanship is warranted for one (1) year from completion date. Manufacturer warranties apply to materials.

4. TIMELINE: Estimated timeline is subject to change based on weather, material availability, and unforeseen conditions.

5. PERMITS: Contractor will obtain all necessary permits. Permit fees are included unless otherwise noted.

6. CHANGE ORDERS: Any modifications to the scope of work will be documented and priced separately.

7. CANCELLATION: Deposits are non-refundable once work has commenced or materials have been ordered.

8. LIABILITY: Contractor maintains general liability and workers' compensation insurance.`}
          </div>
        </div>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          SECTION 9: SIGNATURE
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="px-10 py-6">
        <div 
          className="p-6 rounded-lg"
          style={{
            backgroundColor: "rgba(15, 23, 42, 0.5)",
            borderTop: "2px solid rgba(16, 185, 129, 0.5)"
          }}
        >
          <p className="text-xs text-emerald-400 uppercase tracking-widest mb-6">SIGNATURE</p>
          
          {/* Signature Display Area */}
          <div className="mb-6">
            {/* Handwritten Signature Style */}
            <div 
              className="py-4 mb-4"
              style={{ borderBottom: "1px solid rgba(100, 116, 139, 0.3)" }}
            >
              <p 
                className="text-3xl italic"
                style={{ 
                  fontFamily: "'Brush Script MT', 'Dancing Script', cursive",
                  color: "#10b981"
                }}
              >
                {user?.name || companySettings?.owner_name || "Toby Miller"}
              </p>
            </div>
            
            {/* Signer Details */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-slate-500 text-sm w-20">Name:</span>
                <span className="text-white text-sm font-medium">
                  {user?.name || companySettings?.owner_name || "Toby Miller"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 text-sm w-20">Company:</span>
                <span className="text-white text-sm">
                  {companySettings?.company_name || "Alpha Construction Pros"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 text-sm w-20">Date:</span>
                <span className="text-white text-sm">
                  {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                </span>
              </div>
            </div>
          </div>
          
          {/* Sign Here Button */}
          <button
            className="w-full py-3 rounded font-bold text-sm uppercase transition-all"
            style={{
              background: "linear-gradient(135deg, #d4a84b, #b8963f)",
              color: "#1a1a1a",
              boxShadow: "0 4px 15px rgba(212, 168, 75, 0.3)"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "linear-gradient(135deg, #e0b85c, #c9a650)";
              e.currentTarget.style.boxShadow = "0 6px 20px rgba(212, 168, 75, 0.5)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "linear-gradient(135deg, #d4a84b, #b8963f)";
              e.currentTarget.style.boxShadow = "0 4px 15px rgba(212, 168, 75, 0.3)";
            }}
          >
            Sign here
          </button>
        </div>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          SECTION 10: FOOTER
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div className="px-10 py-8 text-center">
        <p className="text-emerald-400 font-bold mb-2">Thank you for the opportunity!</p>
        <p className="text-slate-500 text-sm">
          This proposal is valid for 30 days from the date above.
        </p>
        <p className="text-slate-600 text-xs mt-4">
          Generated by Contractor Overwatch • {new Date().toLocaleDateString()}
        </p>
      </div>
      
      </div>{/* End of Document Container */}
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          FIXED BOTTOM ACTION BAR
          ═══════════════════════════════════════════════════════════════════════════ */}
      <div 
        className="fixed bottom-0 left-0 right-0 z-[200] px-6 py-4 flex items-center justify-between"
        style={{ 
          backgroundColor: "rgba(15, 23, 42, 0.95)",
          borderTop: "1px solid rgba(16, 185, 129, 0.3)",
          backdropFilter: "blur(10px)"
        }}
      >
        <button
          onClick={onClose}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-all"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="font-medium">← Back</span>
        </button>
        
        <div className="flex items-center gap-3">
          {saving && <span className="text-slate-500 text-sm">Saving...</span>}
          
          <button
            onClick={() => {
              saveProposal({
                payment_milestones: paymentMilestones,
                estimated_duration: estimatedDuration
              });
              setSendModalOpen(true);
            }}
            disabled={!paymentValid}
            className="flex items-center gap-2 px-6 py-2.5 rounded-lg font-bold transition-all hover:scale-105"
            style={{ 
              backgroundColor: paymentValid ? "#10b981" : "#374151",
              color: paymentValid ? "white" : "#6b7280"
            }}
          >
            <Send className="h-4 w-4" />
            Send Proposal
          </button>
        </div>
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          SEND MODAL - Email, SMS, Copy Link
          ═══════════════════════════════════════════════════════════════════════════ */}
      {sendModalOpen && (
        <div 
          className="fixed inset-0 z-[120] flex items-center justify-center overflow-y-auto py-8"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.8)" }}
          onClick={() => setSendModalOpen(false)}
        >
          <div 
            className="w-full max-w-md mx-4 rounded-lg overflow-hidden max-h-[90vh] overflow-y-auto"
            style={{
              backgroundColor: "#0f172a",
              border: "1px solid rgba(16, 185, 129, 0.3)",
              boxShadow: "0 0 40px rgba(16, 185, 129, 0.15)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div 
              className="px-6 py-4 flex items-center justify-between sticky top-0 z-10"
              style={{
                borderBottom: "1px solid rgba(16, 185, 129, 0.2)",
                background: "linear-gradient(180deg, rgba(16, 185, 129, 0.1) 0%, transparent 100%)",
                backgroundColor: "#0f172a"
              }}
            >
              <h3 className="text-white font-bold text-lg">Send / Share</h3>
              <button
                onClick={() => setSendModalOpen(false)}
                className="text-slate-400 hover:text-white text-xl"
              >
                ×
              </button>
            </div>
            
            {/* Send Options */}
            <div className="p-6 space-y-3">
              {/* Send via Email */}
              <button
                onClick={async () => {
                  setSendingVia('email');
                  try {
                    // TODO: Implement actual email sending
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    toast.success("Proposal sent via Email!");
                    setSendModalOpen(false);
                  } catch (error) {
                    toast.error("Failed to send email");
                  } finally {
                    setSendingVia(null);
                  }
                }}
                disabled={sendingVia !== null}
                className="w-full py-4 px-5 rounded-lg text-left transition-all flex items-center justify-between"
                style={{
                  backgroundColor: "rgba(30, 41, 59, 0.5)",
                  border: "1px solid rgba(100, 116, 139, 0.3)"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(16, 185, 129, 0.15)";
                  e.currentTarget.style.borderColor = "rgba(16, 185, 129, 0.5)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(30, 41, 59, 0.5)";
                  e.currentTarget.style.borderColor = "rgba(100, 116, 139, 0.3)";
                }}
              >
                <div>
                  <p className="text-white font-medium">Send via Email</p>
                  <p className="text-slate-500 text-sm">
                    {clientInfo?.email || "No email on file"}
                  </p>
                </div>
                {sendingVia === 'email' ? (
                  <span className="text-emerald-400 text-sm">Sending...</span>
                ) : (
                  <span className="text-slate-500">→</span>
                )}
              </button>
              
              {/* Send via SMS */}
              <button
                onClick={async () => {
                  setSendingVia('sms');
                  try {
                    // TODO: Implement actual SMS sending
                    await new Promise(resolve => setTimeout(resolve, 1000));
                    toast.success("Proposal sent via SMS!");
                    setSendModalOpen(false);
                  } catch (error) {
                    toast.error("Failed to send SMS");
                  } finally {
                    setSendingVia(null);
                  }
                }}
                disabled={sendingVia !== null}
                className="w-full py-4 px-5 rounded-lg text-left transition-all flex items-center justify-between"
                style={{
                  backgroundColor: "rgba(30, 41, 59, 0.5)",
                  border: "1px solid rgba(100, 116, 139, 0.3)"
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(16, 185, 129, 0.15)";
                  e.currentTarget.style.borderColor = "rgba(16, 185, 129, 0.5)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = "rgba(30, 41, 59, 0.5)";
                  e.currentTarget.style.borderColor = "rgba(100, 116, 139, 0.3)";
                }}
              >
                <div>
                  <p className="text-white font-medium">Send via SMS</p>
                  <p className="text-slate-500 text-sm">
                    {clientInfo?.phone || "No phone on file"}
                  </p>
                </div>
                {sendingVia === 'sms' ? (
                  <span className="text-emerald-400 text-sm">Sending...</span>
                ) : (
                  <span className="text-slate-500">→</span>
                )}
              </button>
              
              {/* Copy Link */}
              <button
                onClick={async () => {
                  // Use public_token for the shareable link (backend looks up by public_token)
                  const publicToken = proposal?.public_token;
                  if (!publicToken) {
                    if (!estimateId) {
                      toast.error("Please select a client/estimate first to generate a proposal link.");
                    } else if (loading) {
                      toast.info("Please wait, creating proposal...");
                    } else {
                      toast.error("Proposal link not available. Please wait for the proposal to be created.");
                    }
                    return;
                  }
                  const proposalLink = `${window.location.origin}/proposal/${publicToken}`;
                  try {
                    await navigator.clipboard.writeText(proposalLink);
                    setLinkCopied(true);
                    toast.success("Link copied to clipboard!");
                    setTimeout(() => setLinkCopied(false), 3000);
                  } catch (error) {
                    toast.error("Failed to copy link");
                  }
                }}
                disabled={loading || !proposal?.public_token}
                className="w-full py-4 px-5 rounded-lg text-left transition-all flex items-center justify-between"
                style={{
                  backgroundColor: loading || !proposal?.public_token ? "rgba(30, 41, 59, 0.3)" : "rgba(30, 41, 59, 0.5)",
                  border: "1px solid rgba(100, 116, 139, 0.3)",
                  opacity: loading || !proposal?.public_token ? 0.6 : 1,
                  cursor: loading || !proposal?.public_token ? "not-allowed" : "pointer"
                }}
                onMouseEnter={(e) => {
                  if (!loading && proposal?.public_token) {
                    e.currentTarget.style.backgroundColor = "rgba(16, 185, 129, 0.15)";
                    e.currentTarget.style.borderColor = "rgba(16, 185, 129, 0.5)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = loading || !proposal?.public_token ? "rgba(30, 41, 59, 0.3)" : "rgba(30, 41, 59, 0.5)";
                  e.currentTarget.style.borderColor = "rgba(100, 116, 139, 0.3)";
                }}
              >
                <div>
                  <p className="text-white font-medium">
                    {loading ? "Creating proposal..." : !proposal?.public_token ? "Preparing link..." : "Copy Link"}
                  </p>
                  <p className="text-slate-500 text-sm">
                    {loading ? "Please wait" : !proposal?.public_token ? "Generating shareable link" : "Share a direct link to this proposal"}
                  </p>
                </div>
                {loading ? (
                  <span className="text-amber-400 text-sm animate-pulse">⏳</span>
                ) : linkCopied ? (
                  <span className="text-emerald-400 text-sm">Copied!</span>
                ) : (
                  <span className="text-slate-500">→</span>
                )}
              </button>
            </div>
            
            {/* Smart Tracking Notice */}
            <div 
              className="px-6 py-4 flex items-center gap-3"
              style={{
                backgroundColor: "rgba(16, 185, 129, 0.1)",
                borderTop: "1px solid rgba(16, 185, 129, 0.2)"
              }}
            >
              <div 
                className="w-2 h-2 rounded-full"
                style={{ backgroundColor: "#10b981" }}
              />
              <p className="text-emerald-400 text-sm">
                Smart Tracking Enabled - You&apos;ll be notified when client views this proposal
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          ATTACH FILES MODAL
          ═══════════════════════════════════════════════════════════════════════════ */}
      {attachModalOpen && (
        <div 
          className="fixed inset-0 z-[120] flex items-center justify-center"
          style={{ backgroundColor: "rgba(0, 0, 0, 0.8)" }}
          onClick={() => setAttachModalOpen(false)}
        >
          <div 
            className="w-full max-w-md mx-4 rounded-lg overflow-hidden"
            style={{
              backgroundColor: "#0f172a",
              border: "1px solid rgba(16, 185, 129, 0.3)",
              borderLeft: "4px solid #10b981",
              boxShadow: "0 0 30px rgba(16, 185, 129, 0.1)"
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Top glow line */}
            <div 
              className="h-0.5"
              style={{
                background: "linear-gradient(90deg, transparent, #10b981, transparent)"
              }}
            />
            
            <div className="p-6">
              <h3 className="text-white font-bold text-lg mb-4">Attach files</h3>
              <p className="text-slate-400 text-sm mb-6">
                Upload photos, documents (Word, PDF) to include with this proposal.
              </p>
              
              {/* Upload Area */}
              <div 
                className="border-2 border-dashed border-slate-600 rounded-lg p-8 text-center cursor-pointer hover:border-emerald-500 transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                <p className="text-slate-400 mb-2">Click to upload files</p>
                <p className="text-slate-500 text-xs">PDF, DOC, DOCX, JPG, PNG (max 10MB)</p>
              </div>
              
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                className="hidden"
                onChange={handleFileUpload}
              />
              
              <div className="flex items-center justify-end gap-3 mt-6">
                <button
                  onClick={() => setAttachModalOpen(false)}
                  className="px-5 py-2 text-slate-400 hover:text-white transition-colors border border-slate-700 rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          EDIT MODAL
          ═══════════════════════════════════════════════════════════════════════════ */}
      {editModal.open && (
        <EditModal
          section={editModal.section}
          data={editModal.data}
          onClose={closeEditModal}
          onSave={saveEditModal}
        />
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// EDIT MODAL COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
const EditModal = ({ section, data, onClose, onSave }) => {
  const [value, setValue] = useState(data);
  
  useEffect(() => {
    setValue(data);
  }, [data]);
  
  const getTitle = () => {
    switch (section) {
      case "title": return "Edit Project Title";
      case "client": return "Edit Client Information";
      case "message": return "Edit Client Message";
      case "overview": return "Edit Project Overview";
      case "milestone": return `Edit Milestone ${data?.index + 1}`;
      case "benefit": return `Edit Benefit ${data?.index + 1}`;
      case "terms": return "Edit Terms & Conditions";
      case "duration": return "Edit Estimated Duration";
      default: return "Edit";
    }
  };
  
  const renderContent = () => {
    switch (section) {
      case "title":
        return (
          <input
            type="text"
            value={value || ""}
            onChange={(e) => setValue(e.target.value)}
            className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded text-white"
            placeholder="Project Title"
          />
        );
      
      case "duration":
        return (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-slate-400 text-sm mb-1 block">Minimum Days</label>
                <input
                  type="number"
                  value={value?.min || 0}
                  onChange={(e) => setValue(prev => ({ ...prev, min: parseInt(e.target.value) || 0 }))}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded text-white"
                  min="1"
                />
              </div>
              <div>
                <label className="text-slate-400 text-sm mb-1 block">Maximum Days</label>
                <input
                  type="number"
                  value={value?.max || 0}
                  onChange={(e) => setValue(prev => ({ ...prev, max: parseInt(e.target.value) || 0 }))}
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded text-white"
                  min="1"
                />
              </div>
            </div>
            <p className="text-slate-500 text-sm">
              Duration will display as &quot;{value?.min || 0}-{value?.max || 0} Days&quot;
            </p>
          </div>
        );
        
      case "client":
        return (
          <div className="space-y-4">
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Name</label>
              <input
                type="text"
                value={value?.name || ""}
                onChange={(e) => setValue(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded text-white"
              />
            </div>
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Email</label>
              <input
                type="email"
                value={value?.email || ""}
                onChange={(e) => setValue(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded text-white"
              />
            </div>
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Phone</label>
              <input
                type="tel"
                value={value?.phone || ""}
                onChange={(e) => setValue(prev => ({ ...prev, phone: e.target.value }))}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded text-white"
              />
            </div>
          </div>
        );
        
      case "message":
      case "overview":
      case "terms":
        return (
          <textarea
            value={value || ""}
            onChange={(e) => setValue(e.target.value)}
            className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded text-white min-h-[200px]"
            placeholder={section === "terms" ? "Enter terms and conditions..." : "Enter text..."}
          />
        );
        
      case "milestone":
        return (
          <div className="space-y-4">
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Milestone Name</label>
              <input
                type="text"
                value={value?.milestone?.name || ""}
                onChange={(e) => setValue(prev => ({ 
                  ...prev, 
                  milestone: { ...prev.milestone, name: e.target.value }
                }))}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded text-white"
              />
            </div>
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Day Range</label>
              <input
                type="text"
                value={value?.milestone?.dayRange || ""}
                onChange={(e) => setValue(prev => ({ 
                  ...prev, 
                  milestone: { ...prev.milestone, dayRange: e.target.value }
                }))}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded text-white"
                placeholder="e.g., Days 1-3"
              />
            </div>
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Scope Items (one per line)</label>
              <textarea
                value={(value?.milestone?.scope || []).join("\n")}
                onChange={(e) => setValue(prev => ({ 
                  ...prev, 
                  milestone: { ...prev.milestone, scope: e.target.value.split("\n").filter(s => s.trim()) }
                }))}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded text-white min-h-[120px]"
              />
            </div>
          </div>
        );
        
      case "benefit":
        return (
          <div className="space-y-4">
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Title</label>
              <input
                type="text"
                value={value?.benefit?.title || ""}
                onChange={(e) => setValue(prev => ({ 
                  ...prev, 
                  benefit: { ...prev.benefit, title: e.target.value }
                }))}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded text-white"
              />
            </div>
            <div>
              <label className="text-slate-400 text-sm mb-1 block">Description</label>
              <textarea
                value={value?.benefit?.description || ""}
                onChange={(e) => setValue(prev => ({ 
                  ...prev, 
                  benefit: { ...prev.benefit, description: e.target.value }
                }))}
                className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded text-white min-h-[100px]"
              />
            </div>
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.8)" }}
      onClick={onClose}
    >
      <div 
        className="w-full max-w-lg mx-4 rounded-lg overflow-hidden"
        style={{
          backgroundColor: "#0f172a",
          border: "1px solid rgba(16, 185, 129, 0.3)",
          borderLeft: "4px solid #10b981",
          boxShadow: "0 0 30px rgba(16, 185, 129, 0.1)"
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top glow line */}
        <div 
          className="h-0.5"
          style={{
            background: "linear-gradient(90deg, transparent, #10b981, transparent)"
          }}
        />
        
        <div className="p-6">
          <h3 className="text-white font-bold text-lg mb-6">{getTitle()}</h3>
          
          {renderContent()}
          
          <div className="flex items-center justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-5 py-2 text-slate-400 hover:text-white transition-colors border border-slate-700 rounded"
            >
              Cancel
            </button>
            <button
              onClick={() => onSave(value)}
              className="px-5 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold rounded transition-colors"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProposalBuilder;
